import { useQuery } from '@tanstack/react-query';
import { BookOpen, Download, FileText } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchManuals, manualUrl } from '../../api/client';
import { Card, PageHeader } from '../../ui/components';
import { formatDateTime } from '../../ui/format';
import './manuals.css';

/**
 * User manuals (docs/manuals, user request 2026-10-24, superadmin only): read in the page, download as PDF or DOCX.
 * The HTML is fetched and shown in a sandboxed frame (no scripts, no access to the app).
 */
export function ManualsPage() {
  const { t } = useTranslation();
  const list = useQuery({ queryKey: ['manuals'], queryFn: fetchManuals });
  const items = list.data?.items ?? [];
  const [selected, setSelected] = useState<string | null>(null);
  const current = items.find((m) => `${m.lang}/${m.slug}` === selected) ?? items[0];
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!current?.formats.includes('html')) return;
    let live = true;
    setHtml(null);
    setFailed(false);
    fetch(manualUrl(current.lang, current.slug, 'html'), { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((text) => live && setHtml(text))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [current?.lang, current?.slug, current?.formats]);

  return (
    <div className="page">
      <PageHeader title={t('manuals.title')} subtitle={t('manuals.subtitle')} />
      {list.isLoading && <p className="muted">{t('common.loading')}</p>}
      {list.isError && <p className="cmd-status failed">{t('manuals.loadError')}</p>}
      {list.isSuccess && !items.length && <p className="muted">{t('manuals.empty')}</p>}
      {current && (
        <div className="manuals-layout">
          <Card title={t('manuals.list')}>
            <ul className="manuals-list">
              {items.map((m) => {
                const key = `${m.lang}/${m.slug}`;
                return (
                  <li key={key}>
                    <button
                      type="button"
                      className={`manuals-item${m === current ? ' active' : ''}`}
                      aria-current={m === current}
                      onClick={() => setSelected(key)}
                    >
                      <BookOpen size={16} />
                      <span>
                        <strong>{m.title}</strong>
                        <span className="muted small">
                          {m.lang.toUpperCase()} ·{' '}
                          {t('manuals.updated', { when: formatDateTime(m.updated_at) })}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>
          <Card
            title={current.title}
            actions={
              <span className="toolbar">
                {(['pdf', 'docx'] as const)
                  .filter((f) => current.formats.includes(f))
                  .map((f) => (
                    <a key={f} className="btn" href={manualUrl(current.lang, current.slug, f, true)} download>
                      {f === 'pdf' ? <Download size={14} /> : <FileText size={14} />}{' '}
                      {t(f === 'pdf' ? 'manuals.downloadPdf' : 'manuals.downloadDocx')}
                    </a>
                  ))}
              </span>
            }
            bodyClass="manuals-viewer"
          >
            {failed ? (
              <p className="cmd-status failed">{t('manuals.loadError')}</p>
            ) : html === null ? (
              <p className="muted">{t('common.loading')}</p>
            ) : (
              <iframe title={current.title} sandbox="" srcDoc={html} />
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
